// Keep OlivIA below the original sticky brand and navigation, with its own compact entry.
if (
  typeof customElements !== 'undefined' &&
  !customElements.get('lab-olivia-dock')
) {
  class OliviaDock extends HTMLElement {
    // On a phone product page the entry starts as its tab (data-collapsed-on-phone), so the
    // product reaches the first screen; opening it once keeps it open for the session.
    sessionCollapsed() {
      try {
        if (sessionStorage.getItem('olivia:collapsed') === '1') return true;
        return this.hasAttribute('data-collapsed-on-phone') && sessionStorage.getItem('olivia:opened') !== '1' &&
          matchMedia('(width < 640px)').matches;
      }
      catch { return document.documentElement.hasAttribute('data-olivia-collapsed'); }
    }
    rememberCollapsed(collapsed) {
      document.documentElement.toggleAttribute('data-olivia-collapsed', collapsed);
      try {
        if (collapsed) sessionStorage.setItem('olivia:collapsed', '1');
        else { sessionStorage.removeItem('olivia:collapsed'); sessionStorage.setItem('olivia:opened', '1'); }
      } catch { /* The current page still works when browser storage is unavailable. */ }
    }
    restoreSessionCollapse() {
      if (!this.canCollapse) return;
      const active = document.activeElement;
      const collapsed = this.sessionCollapsed();
      document.documentElement.toggleAttribute('data-olivia-collapsed', collapsed);
      if (collapsed) this.applyCollapsed(true, 'manual');
      else if (this.collapseReason === 'manual') this.applyCollapsed(false);
      // Repair only focus hidden by reconciliation; page content and overlays keep it.
      if (this.host.entryAvailable?.() === false) return;
      if (collapsed && (this.bar.contains(active) || this.host.contains(active))) this.toggle.focus({ preventScroll: true });
      else if (!collapsed && active === this.toggle && this.dataset.collapsed !== 'true') this.focusInput();
    }
    connectedCallback() {
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.bar = this.querySelector('lab-olivia-bar');
      this.row = this.querySelector('.olivia-dock__row');
      this.input = this.querySelector('#olivia-q');
      this.host = document.getElementById(
        this.bar.dataset.layout === 'panel' ? 'olivia-panel' : 'olivia-band',
      );
      this.ask = this.querySelector('.olivia-dock__ask');
      this.toggle = this.querySelector('.olivia-dock__toggle');
      this.collapseButton = this.querySelector('.olivia-dock__collapse');
      this.canCollapse = this.bar.dataset.layout !== 'panel' && !!this.toggle && !!this.collapseButton;
      this.settling = false;
      this.composing = false;
      this.pageScrolled = false;
      this.frame = 0;
      this.settleFrame = 0;
      if (this.canCollapse) {
        this.dataset.collapseReady = 'true';
        this.dataset.collapsed = 'false';
        this.toggle.setAttribute('aria-expanded', 'true');
        this.toggle.addEventListener('click', () => this.showInput(), { signal });
        this.collapseButton.addEventListener('click', () => this.setCollapsed(true, 'manual'), { signal });
        this.input.addEventListener('compositionstart', () => { this.composing = true; this.resetScrollIntent(); }, { signal });
        this.input.addEventListener('compositionend', () => { this.composing = false; this.resetScrollIntent(); }, { signal });
        this.input.addEventListener('input', () => this.resetScrollIntent(), { signal });
      }
      this.resetScrollIntent();
      this.ask?.addEventListener(
        'click',
        () => {
          this.resetScrollIntent();
          this.closeMenu();
          this.host.expand?.();
          this.host.querySelector('#olivia-c')?.focus({ preventScroll: true });
        },
        { signal },
      );
      this.addEventListener(
        'focusin',
        () => {
          this.resetScrollIntent();
          this.closeMenu();
          this.measure();
        },
        { signal },
      );
      this.addEventListener(
        'focusout',
        () => {
          this.resetScrollIntent();
          requestAnimationFrame(() => this.measure());
        },
        { signal },
      );
      this.observer = new MutationObserver(() => this.syncConversation());
      this.observer.observe(this.bar, {
        attributes: true,
        attributeFilter: ['hidden', 'data-panel'],
      });
      this.resize = new ResizeObserver(() => this.measure());
      this.resize.observe(this);
      this.resize.observe(this.row);
      const header = document.querySelector('.lab-header-section');
      if (header) this.resize.observe(header);
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.measure();
          if (this.pageScrolled) {
            this.pageScrolled = false;
            this.onPageScroll();
          }
        });
      };
      window.addEventListener('scroll', () => { this.pageScrolled = true; schedule(); }, { signal, passive: true });
      const viewportChanged = () => { this.resetScrollIntent(); schedule(); };
      window.addEventListener('resize', viewportChanged, { signal, passive: true });
      window.visualViewport?.addEventListener('resize', viewportChanged, {
        signal,
        passive: true,
      });
      window.visualViewport?.addEventListener('scroll', schedule, {
        signal,
        passive: true,
      });
      // A cached document can predate the visitor's choice on a later page.
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) this.restoreSessionCollapse();
        this.measure();
      }, { signal });
      this.restoreSessionCollapse();
      this.syncConversation();
      this.measure();
    }
    disconnectedCallback() {
      this.controller.abort();
      this.observer.disconnect();
      this.resize.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.settleFrame);
      if (this.canCollapse) {
        this.bar.inert = false;
        this.bar.removeAttribute('aria-hidden');
        this.collapseButton.setAttribute('aria-expanded', 'true');
        delete this.dataset.collapseReady;
        delete this.dataset.collapsed;
      }
      document.documentElement.style.removeProperty('--chrome-h');
      document.documentElement.style.removeProperty('--olivia-viewport-top');
      document.documentElement.removeAttribute('data-olivia-keyboard');
    }
    measure() {
      if (!this.isConnected) return;
      const header = document.querySelector('.lab-header-section');
      const chrome = document.querySelector('.lab-chrome__unit');
      const headerHeight = chrome?.getBoundingClientRect().height ?? 0;
      const viewport = window.visualViewport;
      const keyboard =
        !!viewport &&
        innerHeight - viewport.height > 120 &&
        this.contains(document.activeElement) &&
        document.activeElement.matches('input, textarea');
      document.documentElement.toggleAttribute(
        'data-olivia-keyboard',
        keyboard,
      );
      const top = keyboard ? viewport.offsetTop : 0;
      document.documentElement.style.setProperty(
        '--olivia-viewport-top',
        `${top}px`,
      );
      this.dataset.stuck = String(
        !header || header.getBoundingClientRect().top <= top + 1,
      );
      this.style.setProperty('--olivia-header-height', `${headerHeight}px`);
      this.style.setProperty('--olivia-dock-top', `${top + headerHeight}px`);
      const dockRect = this.getBoundingClientRect();
      const rowRect = this.row.getBoundingClientRect();
      this.style.setProperty(
        '--olivia-row-height',
        `${rowRect.height}px`,
      );
      document.documentElement.style.setProperty(
        '--chrome-h',
        `${headerHeight + dockRect.height}px`,
      );
      const menu = document.querySelector('lab-header');
      if (menu?.isOpen()) menu.placeUnderChrome();
    }
    closeMenu() {
      const header = document.querySelector('lab-header');
      if (!header) return;
      header.opener = null;
      header.close();
    }
    showInput() {
      if (this.host.entryAvailable?.() === false) return;
      this.setCollapsed(false, 'manual');
      this.resetScrollIntent();
      this.focusInput();
    }
    focusInput() {
      const target = this.input.disabled
        ? this.bar.querySelector('.olivia-bar__resume:not([hidden])') ?? this.collapseButton
        : this.input;
      target?.focus({ preventScroll: true });
    }
    resetScrollIntent() {
      this.lastScrollY = window.scrollY;
      this.downward = 0;
    }
    onPageScroll() {
      const delta = window.scrollY - this.lastScrollY;
      this.lastScrollY = window.scrollY;
      if (!this.canCollapse || this.settling || Math.abs(delta) < 2) return;
      if (this.dataset.collapsed === 'true') {
        // Upward reading keeps the compact control stable. Only returning to the
        // original header restores an automatically hidden entry.
        if (this.collapseReason === 'auto' && (this.dataset.stuck !== 'true' || window.scrollY <= 1) && delta < 0 &&
            !this.contains(document.activeElement) && this.host.entryAvailable?.()) this.setCollapsed(false);
        return;
      }
      if (delta < 0 || this.dataset.stuck !== 'true' || !this.host.hidden ||
          ['sending', 'streaming'].includes(this.host.view?.phase) || this.host.loading || this.input.value !== '' ||
          this.composing || this.contains(document.activeElement) || !this.host.entryAvailable?.()) {
        this.downward = 0;
        return;
      }
      this.downward += delta;
      // Roughly another screen of browsing after the entry becomes sticky.
      if (this.downward >= Math.max(240, (window.visualViewport?.height ?? innerHeight) * 0.75)) this.setCollapsed(true, 'auto');
    }
    setCollapsed(collapsed, reason = null) {
      if (!this.canCollapse || this.host.entryAvailable?.() === false) return;
      if (reason === 'manual') this.rememberCollapsed(collapsed);
      this.applyCollapsed(collapsed, reason, collapsed && reason === 'manual');
    }
    applyCollapsed(collapsed, reason = null, focus = false) {
      if (collapsed) this.host.reduce?.(false);
      if ((this.dataset.collapsed === 'true') === collapsed) {
        if (collapsed) this.collapseReason = reason;
        return;
      }
      this.collapseReason = collapsed ? reason : null;
      // The dock is in flow, so its height change would move the whole page under a
      // sticky header that scroll anchoring picks as its anchor (0.16 CLS on phones).
      // Scroll by the same amount in the same frame so the content stays put.
      const main = document.getElementById('MainContent');
      const before = main?.getBoundingClientRect().top;
      this.dataset.collapsed = String(collapsed);
      this.bar.inert = collapsed;
      this.bar.toggleAttribute('aria-hidden', collapsed);
      if (collapsed) this.bar.setAttribute('aria-hidden', 'true');
      this.toggle.setAttribute('aria-expanded', String(!collapsed));
      this.collapseButton.setAttribute('aria-expanded', String(!collapsed));
      // Neither height nor the page animates. Ignore layout/scroll-anchor adjustments
      // from this change so they cannot trigger another collapse or reopen.
      this.settling = true;
      this.measure();
      if (main) window.scrollBy(0, main.getBoundingClientRect().top - before);
      this.resetScrollIntent();
      if (focus) this.toggle.focus({ preventScroll: true });
      cancelAnimationFrame(this.settleFrame);
      this.settleFrame = requestAnimationFrame(() => {
        this.settleFrame = requestAnimationFrame(() => {
          this.settling = false;
          this.resetScrollIntent();
        });
      });
    }
    revealAnswer() {
      this.measure();
      if (this.host.hidden) return;
      const viewport = window.visualViewport;
      const bottom = (viewport?.offsetTop ?? 0) + (viewport?.height ?? innerHeight);
      const strip = document.querySelector('lab-bottom-strip');
      const stripHeight = strip && getComputedStyle(strip).display !== 'none'
        ? strip.getBoundingClientRect().height : 0;
      const answerTop = this.host.getBoundingClientRect().top;
      const rowBottom = this.row.getBoundingClientRect().bottom;
      if (answerTop >= rowBottom - 1 && answerTop < bottom - stripHeight - 60) return;
      const pin = parseFloat(this.style.getPropertyValue('--olivia-dock-top')) || 0;
      // Reveal the start once on a voluntary send or restore. Streamed text and cards
      // grow naturally; reading never chases the end or creates an inner scrollbar.
      window.scrollTo({ top: Math.max(0, window.scrollY + answerTop - pin - this.row.getBoundingClientRect().height), behavior: 'instant' });
      this.measure();
    }
    syncConversation() {
      const open = this.bar.dataset.layout === 'panel' ? this.bar.hidden :
        this.bar.dataset.panel === 'open' || this.bar.dataset.panel === 'resuming';
      this.dataset.conversation = String(open);
      if (open) {
        if (this.dataset.collapsed === 'true' && this.collapseReason === 'manual') this.host.reduce?.(false);
        else this.setCollapsed(false);
      }
      if (this.ask) {
        this.ask.setAttribute('aria-expanded', String(this.bar.hidden));
        this.ask.setAttribute('aria-label', this.ask.dataset.resumeLabel);
        this.ask.querySelector('[data-dock-entry]').hidden = true;
        this.ask.querySelector('[data-dock-continue]').hidden = false;
      }
      this.measure();
    }
  }
  const define = () => {
    if (!customElements.get('lab-olivia-dock'))
      customElements.define('lab-olivia-dock', OliviaDock);
  };
  if (document.prerendering)
    document.addEventListener('prerenderingchange', define, { once: true });
  else define();
}
